'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Nanoplastics Crossed a Placenta Model and Changed Hormone Output: The 2026 Study, Carefully"
      author="MicroPlastics Research Desk"
      date="August 10, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/nanoplastics-placenta-hormones-study-2026.webp"
      heroImageAlt="A three-layer laboratory barrier model with colored particles and a gloved researcher"
    >
      <BlogLastUpdated iso="2026-08-10" formatted="August 10, 2026" />

      <BlogTLDR>
        <strong>A new study built a three-layer human cell model of the
        mother–placenta–fetus interface and found that four of five plastic
        polymers crossed it within 72 hours — and changed the hormones the fetal
        cells produced.</strong> Polystyrene, PVC, PMMA and PET all passed
        through; polyamide could not be reliably measured. Polystyrene mainly{' '}
        <strong>reduced estrogen</strong>; the others mainly reduced{' '}
        <strong>etiocholanolone</strong>, a steroid metabolite. This matters
        because it moves the field past &ldquo;plastic is present&rdquo; toward{' '}
        &ldquo;plastic does something.&rdquo; It is also{' '}
        <strong>a dish of cells, not a pregnancy</strong>. No woman was exposed,
        no baby was affected, and the authors say so explicitly. If you are
        pregnant and looking for what to do differently this week, the honest
        answer is that this study does not change the advice — but it does
        strengthen the reason behind it.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Published in <strong>Molecular and Cellular Endocrinology</strong> on 20 July 2026 by van Boxel and colleagues at Vrije Universiteit Amsterdam, part of the EU <strong>AURORA</strong> project.</>,
          <>The model combines <strong>three human cell lines</strong>: two forming the placental barrier and one representing fetal steroid-producing tissue.</>,
          <><strong>Polystyrene, PVC, PMMA and PET crossed the barrier.</strong> Polyamide could not be reliably quantified — which is not the same as &ldquo;it did not cross.&rdquo;</>,
          <>Crossing happened within <strong>72 hours</strong>.</>,
          <><strong>Polystyrene primarily decreased estrogen.</strong> The other polymers primarily reduced etiocholanolone.</>,
          <>The authors state plainly that this <strong>does not show harm in pregnancy</strong>, and that the mechanism is still unknown.</>,
          <>This complements, rather than replaces, the <Link href="/blog/microplastics-in-placenta" className="text-forest underline">2024 UNM study that found particles in 62 of 62 human placentas</Link>. That one showed presence; this one probes function.</>,
        ]}
      />

      <KeyStats
        heading="What the study reported"
        stats={[
          { value: '4 of 5', label: 'polymers crossed the barrier model', context: 'polystyrene, PVC, PMMA and PET; polyamide could not be reliably quantified', source: 'van Boxel et al., 2026', sourceUrl: 'https://doi.org/10.1016/j.mce.2026.112871' },
          { value: '72 hours', label: 'timeframe for crossing', context: 'the exposure window used in the in vitro model', source: 'van Boxel et al., 2026', sourceUrl: 'https://doi.org/10.1016/j.mce.2026.112871' },
          { value: '3', label: 'human cell lines in the model', context: 'two placental barrier layers plus fetal steroidogenic cells', source: 'AURORA project, VU Amsterdam', sourceUrl: 'https://auroraresearch.eu/aurora-research-finds-micro-and-nanoplastics-can-cross-placental-barrier-and-alter-hormone-production/' },
          { value: '0', label: 'pregnant women exposed in this study', context: 'it is a laboratory cell model, not a human trial', source: 'van Boxel et al., 2026', sourceUrl: 'https://doi.org/10.1016/j.mce.2026.112871' },
        ]}
      />

      <h2>What is genuinely new here</h2>
      <p>
        We already knew microplastics reach the placenta. A 2024 University of
        New Mexico study found them in{' '}
        <Link href="/blog/microplastics-in-placenta" className="text-forest underline">every one of 62 human placentas tested</Link>,
        averaging around 127 micrograms per gram. That was a detection finding:
        the particles are there.
      </p>
      <p>
        Detection studies leave the obvious question open. Being present in
        tissue is not the same as passing through it, and passing through is not
        the same as doing anything. This study goes after the second and third
        parts.
      </p>
      <p>
        The team built a model with{' '}
        <strong>three layers of human cells</strong> — two representing the
        placental barrier and one representing the fetal tissue that makes
        steroid hormones — and then put five different plastic polymers on the
        maternal side to see what arrived on the other side and what happened
        when it did.
      </p>

      <h2>The results</h2>

      <BlogComparisonTable
        caption="Polymers tested in the feto-placental model and what was observed (van Boxel et al., Molecular and Cellular Endocrinology, July 2026)"
        columns={[
          { key: 'polymer', label: 'Polymer' },
          { key: 'common', label: 'Where you meet it' },
          { key: 'crossed', label: 'Crossed the barrier?' },
          { key: 'hormone', label: 'Hormone effect reported' },
        ]}
        rows={[
          { polymer: 'Polystyrene (PS)', common: 'Foam takeaway containers, disposable cups and cutlery', crossed: 'Yes', hormone: 'Primarily decreased estrogen' },
          { polymer: 'PVC', common: 'Tubing, cling film, flooring, some packaging', crossed: 'Yes', hormone: 'Primarily reduced etiocholanolone' },
          { polymer: 'PMMA', common: 'Acrylic; also a soft-focus particle in cosmetics and SPF', crossed: 'Yes', hormone: 'Primarily reduced etiocholanolone' },
          { polymer: 'PET', common: 'Drink bottles, food trays, polyester textiles', crossed: 'Yes', hormone: 'Primarily reduced etiocholanolone' },
          { polymer: 'Polyamide (PA)', common: 'Nylon — textiles, tea bags, cosmetics', crossed: 'Could not be reliably quantified', hormone: 'Not established' },
        ]}
      />

      <p>
        That polyamide row deserves care.{' '}
        <strong>&ldquo;Could not be reliably quantified&rdquo; is not a clean
        result.</strong> It means the measurement did not work well enough to
        report, not that nylon stayed put. Coverage that lists nylon as
        &ldquo;safe&rdquo; because it is not in the crossed column is misreading
        the paper.
      </p>

      <RiskQuizInlineNudge source="blog_inline_placenta_2026" />

      <h2>Why a hormone finding is the significant part</h2>
      <p>
        Estrogen and the steroid pathway are not incidental to pregnancy — they
        run it. Steroid hormones govern implantation, placental development, and
        the timing of fetal development including the reproductive and nervous
        systems. A change in steroid output is not a peripheral effect; it is a
        change to the control system.
      </p>
      <p>
        Which is exactly why it needs to be reported carefully rather than
        loudly. The finding is that{' '}
        <strong>hormone production changed in cultured cells</strong> exposed to
        plastic particles for three days. It is not that pregnancies were
        affected, that estrogen fell in any person, or that any outcome in a baby
        was measured. Those studies have not been done.
      </p>

      <h2>What this study cannot tell you</h2>
      <p>
        We think being direct about limitations is what makes the rest of a page
        like this trustworthy, so here they are plainly:
      </p>
      <ul>
        <li>
          <strong>It is cells in a dish.</strong> A three-layer culture is an
          impressive model and still not a placenta, which is a living organ with
          blood flow, immune activity and a maternal body attached.
        </li>
        <li>
          <strong>Dose is the perennial problem.</strong> In vitro work typically
          uses particle concentrations chosen to produce a measurable signal.
          Whether they match real-world exposure is usually unresolved, and it is
          unresolved here.
        </li>
        <li>
          <strong>The particles were pristine.</strong> Laboratory polymer beads
          are uniform and clean. Environmental microplastics are weathered,
          irregular and coated with whatever they have picked up.
        </li>
        <li>
          <strong>Seventy-two hours is not nine months.</strong> The authors
          themselves call for longer-term exposure work.
        </li>
        <li>
          <strong>The mechanism is unknown.</strong> Stated explicitly in the
          paper. Nobody yet knows how the particles alter steroid output.
        </li>
      </ul>
      <p>
        The lead author&apos;s own framing is the fairest summary anyone has
        given: the work shows several polymers can cross a human placental model
        and influence hormone production, and{' '}
        <em>&ldquo;while this does not directly mean they cause harm during
        pregnancy, it highlights the urgent need to better understand how these
        particles may affect fetal development.&rdquo;</em>
      </p>

      <BlogAppCTA
        headline="Reduce what you can control"
        sub="Scan the bottles, containers and packaging you use daily with MicroPlastics to find the polystyrene, PVC and PET in your routine — and the swaps that are actually worth making."
      />

      <h2>What a pregnant person should actually do</h2>
      <p>
        Nothing in this study warrants panic, and we would rather say that than
        farm anxiety. But three of the four polymers that crossed are ones you
        genuinely can reduce, and the reductions are cheap:
      </p>
      <ul>
        <li>
          <strong>Polystyrene is the easiest win.</strong> Foam takeaway boxes
          and disposable cups, especially with hot food. This is the polymer with
          the estrogen finding and the one most avoidable.
        </li>
        <li>
          <strong>Do not heat food in plastic.</strong> Microwaving in a
          container is the highest-release everyday act in most kitchens. Glass
          or ceramic costs nothing to switch to.
        </li>
        <li>
          <strong>PET: stop reusing single-use bottles.</strong> A refilled,
          scratched, warm bottle sheds far more than a fresh one. See{' '}
          <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-forest underline">what happens to a bottle left in a hot car</Link>.
        </li>
        <li>
          <strong>Keep taking your prenatal care as advised.</strong> Nothing
          here overrides medical guidance, and nothing here justifies avoiding
          nutritious packaged food.
        </li>
        <li>
          <strong>Do not let this ruin your pregnancy.</strong> Stress has
          well-documented effects on pregnancy outcomes. Microplastic exposure at
          this stage of evidence does not.
        </li>
      </ul>

      <h2>One connection worth flagging</h2>
      <p>
        PMMA — one of the polymers that crossed — is the same soft-focus particle
        we found this month in{' '}
        <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">two Cetaphil SPF moisturisers</Link>{' '}
        and a{' '}
        <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe SPF lotion</Link>.
      </p>
      <p>
        We want to be careful not to overstate that link, because the routes are
        different: this study introduced particles to a barrier model directly,
        whereas a cosmetic sits on intact skin, which is a{' '}
        <Link href="/blog/microplastics-and-skin-absorption" className="text-forest underline">genuinely effective barrier for particles of this size</Link>.
        A PMMA sunscreen is not a plausible route to the placenta on current
        evidence. It is still worth knowing that the polymer being studied in a
        fetal model is one that also appears in daily-use products, because that
        is the kind of overlap that decides which questions get funded next.
      </p>

      <p>
        Background reading:{' '}
        <Link href="/blog/microplastics-in-placenta" className="text-forest underline">the 2024 UNM placenta detection study</Link>,{' '}
        <Link href="/blog/microplastics-in-human-blood" className="text-forest underline">microplastics in human blood</Link>,
        and our{' '}
        <Link href="/microplastics-pregnancy" className="text-forest underline">pregnancy guide</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics cross the placenta?',
            answer: 'In a laboratory model, yes. A July 2026 study from Vrije Universiteit Amsterdam built a three-layer human cell model of the placental barrier and found that polystyrene, PVC, PMMA and PET all crossed it within 72 hours. Polyamide could not be reliably quantified. This is a cell culture model rather than a pregnancy, so it demonstrates that crossing is possible under these conditions, not that it happens at the same rate in a living placenta.',
          },
          {
            question: 'Does this study mean microplastics harm babies?',
            answer: 'No, and the authors say so directly. The study found that hormone production changed in cultured fetal cells after exposure, but it did not involve any pregnant women or measure any outcome in a baby. Lead author Jeske van Boxel stated that the results do not directly mean the particles cause harm during pregnancy, while noting they show an urgent need to understand the effects better.',
          },
          {
            question: 'Which plastic was worst in the placenta study?',
            answer: 'Polystyrene stands out because it was the polymer primarily associated with decreased estrogen, while PVC, PMMA and PET were primarily associated with reduced etiocholanolone. Estrogen has a more directly recognised role in maintaining pregnancy, which makes the polystyrene result the one most worth following up. Polystyrene is also the easiest of the four to avoid, since it is mostly foam takeaway containers and disposable cups.',
          },
          {
            question: 'What should I avoid during pregnancy because of this?',
            answer: 'The practical steps have not changed: avoid heating food in plastic, skip polystyrene foam containers and disposable cups especially for hot food and drink, and do not reuse scratched single-use PET bottles or leave them in hot cars. None of these require buying anything. This study strengthens the rationale behind that advice rather than adding new instructions, and it does not justify avoiding nutritious packaged foods or changing medical guidance.',
          },
          {
            question: 'How is this different from the 2024 placenta study?',
            answer: 'The 2024 University of New Mexico study analysed 62 real human placentas and found microplastics in all of them, at an average of roughly 127 micrograms per gram — a detection result showing the particles are present. The 2026 study is a laboratory model asking a different question: whether particles can pass through the placental barrier and whether doing so changes hormone production. One establishes presence in real tissue; the other probes mechanism in an artificial system.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'van Boxel, J. et al.', year: 2026, title: 'Effects of different micro- and nanoplastic polymers on steroidogenesis in a feto-placental in vitro model', publisher: 'Molecular and Cellular Endocrinology', url: 'https://doi.org/10.1016/j.mce.2026.112871' },
          { authors: 'AURORA project (Vrije Universiteit Amsterdam)', year: 2026, title: 'AURORA research finds micro- and nanoplastics can cross placental barrier and alter hormone production', publisher: 'auroraresearch.eu', url: 'https://auroraresearch.eu/aurora-research-finds-micro-and-nanoplastics-can-cross-placental-barrier-and-alter-hormone-production/' },
          { authors: 'Garcia, M.A. et al.', year: 2024, title: 'Quantitation and identification of microplastics accumulation in human placental specimens (62 of 62 positive, mean ~127 µg/g)', publisher: 'Toxicological Sciences', url: 'https://doi.org/10.1093/toxsci/kfae021' },
        ]}
      />
    </BlogPostLayout>
  )
}
